import { useState, useEffect, useCallback } from "react"

export const useProductAvailability = (product, variants, productVariant) => {
  const [variantAvailable, setVariantAvailable] = useState(
    productVariant.availableForSale,
  )
  const [anyVariantAvailable, setAnyVariantAvailable] = useState(false)
  const [variantAvailabilityMap, setVariantAvailabilityMap] = useState({})
  const countryCode = process.env.GATSBY_LOCALE.toUpperCase()

  const matchesProductVariant = (fetchedVariantId, productVariantId) => {
    return (
      fetchedVariantId === productVariantId ||
      fetchedVariantId.endsWith(productVariantId)
    )
  }

  const checkAvailability = useCallback(async () => {
    if (!product.shopifyId) {
      console.error("Product ID is missing!")
      return
    }

    try {
      const response = await fetch("/.netlify/functions/get-inventory", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          productGid: product.shopifyId,
          countryCode: countryCode,
        }),
      })

      const inventoryData = await response.json()

      const availabilityMap = inventoryData?.length
        ? Object.fromEntries(
            inventoryData.map(({ variantId, available }) => [
              variantId,
              available > 0,
            ])
          )
        : {}

      setVariantAvailabilityMap(availabilityMap)

      const result = inventoryData.filter(variant =>
        matchesProductVariant(variant.variantId, productVariant.shopifyId),
      )

      if (result.length > 0) {
        const isAvailable = result[0].available > 0
        setVariantAvailable(isAvailable)

        if (!isAvailable) {
          const fallback = inventoryData.find(v => v.available > 0)
          if (fallback) {
            const matched = variants.find(v =>
              fallback.variantId.endsWith(v.shopifyId),
            )
            if (matched) {
              if (typeof window !== "undefined" && window.setVariantExternally) {
                window.setVariantExternally(matched)
              }
            }
          }
        }
      } else {
        console.warn("No matching variant found in inventory data.")
        setVariantAvailable(false)
      }
    } catch (error) {
      console.error("GraphQL error:", error)
    }
  }, [product.shopifyId, productVariant.shopifyId])

  const checkProductAvailability = useCallback(() => {
    const isAnyVariantAvailable = variants.some(
      variant => variant.availableForSale,
    )
    setAnyVariantAvailable(isAnyVariantAvailable)
  }, [variants])

  useEffect(() => {
    checkAvailability()
    checkProductAvailability()
  }, [checkAvailability, checkProductAvailability])

  return {
    variantAvailable,
    anyVariantAvailable,
    variantAvailabilityMap,
  }
}
